import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, Loader2, MessageSquare, RotateCw, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, GUEST_KEY } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { normalizePhone } from "@/pages/Registrering";
import { useLang } from "@/lib/i18n";
import { BackButton } from "@/components/LanguageToggle";

const input =
  "h-14 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-base text-[#FDFBF7] placeholder:text-[#A8A29E]/50 transition-colors focus:border-[#D4AF37] focus:outline-none";
const primary =
  "flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-base font-700 uppercase tracking-wide text-[#1A1715] transition-colors hover:bg-[#E5C564] disabled:cursor-not-allowed disabled:bg-[#D4AF37]/25 disabled:text-[#1A1715]/50";

function PhoneStep({ onSent }) {
  const { t } = useLang();
  const [fornavn, setFornavn] = useState("");
  const [telefon, setTelefon] = useState("");
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const clean = normalizePhone(telefon);
  const valid = fornavn.trim().length >= 2 && /^[2-9]\d{7}$/.test(clean);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setNotFound(false);
    try {
      const res = await api.post("/guest/login/start", { fornavn: fornavn.trim(), telefon: clean });
      onSent({ loginId: res.data.login_id, telefon: clean });
    } catch (err) {
      if (err.response?.status === 404 && err.response?.data?.detail === "not_registered") setNotFound(true);
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 text-left" data-testid="login-form">
      <div>
        <label className="mb-2 block text-sm font-medium text-[#FDFBF7]">{t("Fornavn", "First name")}</label>
        <input className={input} value={fornavn} onChange={(e) => setFornavn(e.target.value)} placeholder="Ola" autoComplete="given-name" data-testid="login-fornavn" />
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-[#FDFBF7]">{t("Telefonnummer", "Phone number")}</label>
        <input className={input} value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="+47 400 00 000" inputMode="tel" autoComplete="tel" data-testid="login-telefon" />
      </div>
      {notFound && (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-4 text-sm text-[#E5C564]" data-testid="login-not-registered">
          {t("Vi fant ingen registrering med dette telefonnummeret.", "We couldn't find a registration with this phone number.")}
          <Link to="/gave/registrer" className="mt-3 flex h-11 items-center justify-center gap-2 rounded-full bg-[#D4AF37] font-semibold text-[#1A1715] hover:bg-[#E5C564]" data-testid="login-go-register-btn">
            <UserPlus className="h-4 w-4" /> {t("Registrer deg her", "Sign up here")}
          </Link>
        </div>
      )}
      <button type="submit" disabled={!valid || busy} className={primary} data-testid="login-send-code-btn">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("Sender…", "Sending…")}</> : t("Send meg kode på SMS", "Text me a code")}
      </button>
    </form>
  );
}

function CodeStep({ loginId, telefon, onBack }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const confirm = async (value) => {
    setBusy(true);
    try {
      const res = await api.post("/guest/login/confirm", { login_id: loginId, code: value });
      localStorage.setItem(GUEST_KEY, res.data.token);
      navigate("/mine-kuponger", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCode("");
      setBusy(false);
    }
  };

  const onChange = (v) => {
    setCode(v);
    if (v.length === 6 && !busy) confirm(v);
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/guest/login/resend", { login_id: loginId });
      toast.success(t("Ny kode er sendt", "A new code has been sent"));
      setCooldown(60);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const slot = "h-14 w-11 border-white/15 bg-[#1A1715] text-2xl text-[#FDFBF7] first:rounded-l-xl last:rounded-r-xl sm:w-12";
  return (
    <div className="flex flex-col items-center gap-6" data-testid="login-code-step">
      <span className="inline-flex items-center gap-2 text-sm text-[#A8A29E]"><MessageSquare className="h-4 w-4 text-[#D4AF37]" /> {t("Kode sendt på SMS til", "Code texted to")} {telefon}</span>
      <InputOTP maxLength={6} value={code} onChange={onChange} disabled={busy} data-testid="login-otp-input" autoFocus>
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className={slot} />)}
        </InputOTPGroup>
      </InputOTP>
      <button type="button" onClick={() => confirm(code)} disabled={code.length !== 6 || busy} className={primary} data-testid="login-confirm-btn">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("Logger inn…", "Logging in…")}</> : t("Logg inn", "Log in")}
      </button>
      <button type="button" onClick={resend} disabled={cooldown > 0 || busy} className="inline-flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#D4AF37] disabled:opacity-60" data-testid="login-resend-btn">
        <RotateCw className="h-4 w-4" /> {cooldown > 0 ? `${t("Send koden på nytt", "Resend code")} (${cooldown}s)` : t("Send koden på nytt", "Resend code")}
      </button>
      <button type="button" onClick={onBack} className="text-xs text-[#A8A29E] hover:text-[#D4AF37]" data-testid="login-change-number-btn">{t("Bruk et annet nummer", "Use a different number")}</button>
    </div>
  );
}

export default function LoggInn() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(GUEST_KEY)) navigate("/mine-kuponger", { replace: true });
  }, [navigate]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="relative flex items-center justify-center animate-fade-in"><BackButton testid="login-top-back-btn" /><Logo /></header>
        <main className="mt-10 flex-1 text-center">
          <div className="animate-fade-up">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <LogIn className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl font-600 text-[#FDFBF7] sm:text-5xl">{t("Logg inn", "Log in")}</h1>
            <p className="mx-auto mt-4 max-w-md text-base text-[#FDFBF7]/90">
              {sent
                ? t("Skriv inn den 6-sifrede koden vi sendte deg.", "Enter the 6-digit code we sent you.")
                : t("Skriv inn navn og telefonnummer, så sender vi deg en kode på SMS.", "Enter your name and phone number and we'll text you a code.")}
            </p>
          </div>
          <div className="mx-auto mt-8 max-w-sm animate-fade-up rounded-3xl border border-white/10 bg-[#282421]/90 p-6 shadow-2xl sm:p-8" style={{ animationDelay: "0.15s" }}>
            {sent ? <CodeStep {...sent} onBack={() => setSent(null)} /> : <PhoneStep onSent={setSent} />}
          </div>
        </main>
        <footer className="mt-8 text-center text-xs text-[#A8A29E]/60">
          <Link to="/gave" className="hover:text-[#D4AF37]" data-testid="login-back-btn">← {t("Tilbake", "Back")}</Link>
        </footer>
      </div>
    </div>
  );
}
